// File: src/components/admin/analysis-tasks/AnalysisTaskDetailView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Detail/edit view for a single AI analysis task with tabbed form

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Chip, CircularProgress, Alert, Snackbar,
    TextField, MenuItem, FormControlLabel, Slider, Divider, Switch,
    FormControl, FormLabel, RadioGroup, Radio, FormGroup, Checkbox,
    Tabs, Tab, Grid, Card, CardContent
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayArrowIcon,
    Psychology as PsychologyIcon,
    Build as BuildIcon,
    Groups as GroupsIcon,
    Notifications as NotificationsIcon,
    Science as ScienceIcon,
    AutoAwesome as AutoAwesomeIcon,
    ContentCopy as ContentCopyIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';
import { mainApi } from '../../../services/mainApi';
import ValidationPanel from './ValidationPanel';
import AiAssistPanel from './AiAssistPanel';
import AudienceRuleBuilder from './AudienceRuleBuilder';
import TaskScheduleTab from './TaskScheduleTab';
import ScheduleBuilder from './ScheduleBuilder';

// removed src/components/admin/subscription/utils.js; this is the
// only remaining consumer of getAllModels, so inline a minimal replacement.
const FALLBACK_PROVIDER_MODELS = {
    openai: [
        { value: 'gpt-4o', label: 'GPT-4o (Omni)' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        { value: 'gpt-4', label: 'GPT-4' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    ],
    anthropic: [
        { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
        { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
        { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
    ],
    google: [
        { value: 'gemini-1.5-pro-latest', label: 'Gemini 1.5 Pro' },
        { value: 'gemini-1.5-flash-latest', label: 'Gemini 1.5 Flash' },
        { value: 'gemini-pro', label: 'Gemini Pro' },
    ],
};
const getAllModels = (edenConfig) => {
    if (edenConfig?.modelsMetadata?.textProviders) {
        return Object.entries(edenConfig.modelsMetadata.textProviders).flatMap(([provider, data]) =>
            (data.models || []).map(model => ({ ...model, provider }))
        );
    }
    return Object.keys(FALLBACK_PROVIDER_MODELS).flatMap(provider =>
        FALLBACK_PROVIDER_MODELS[provider].map(model => ({ ...model, provider }))
    );
};

const TAB_NAMES = [
    'ai-config',       // 0
    'tools-data',      // 1
    'audience',        // 2
    'communication',   // 3
    'testing',         // 4
    'schedule',        // 5
];

const TAB_LABELS = [
    { label: 'AI Config', icon: <PsychologyIcon /> },
    { label: 'Tools & Data', icon: <BuildIcon /> },
    { label: 'Audience', icon: <GroupsIcon /> },
    { label: 'Communication', icon: <NotificationsIcon /> },
    { label: 'Testing', icon: <ScienceIcon /> },
    { label: 'Schedule', icon: <ScheduleIcon /> },
];

const DEFAULT_FORM_DATA = {
    name: '',
    description: '',
    status: 'paused',
    // Tab 0: AI Config
    systemPrompt: '',
    responseStyle: '',
    aiModel: '',
    aiProvider: 'openai',
    temperature: 0.7,
    maxTokens: 1000,
    // Tab 1: Tools & Data Access
    dataAccessTier: 'content',
    enabledTools: [],
    // Autonomous: when off (default), write tools produce approval proposals;
    // when on, the task executes writes live using its granted scopes.
    autonomous: false,
    // Tab 2: Audience
    audienceRules: [],
    subscriptionPlans: ['all'],
    // Tab 3: Communication
    channels: ['in_app'],
    notificationType: 'AGENT_MESSAGE',
    emailSubjectTemplate: {},
    maxEmailsPerDay: 1,
    maxNotificationsPerDay: 1,
    cooldownAfterEngagement: '4h',
    maxConsecutiveIgnored: 3,
    // Tab 4: Testing (transient state, not saved)
};

// Human-readable labels for the permission scopes the Tool Core catalog is
// grouped by (fetched from GET /admin/analysis-tasks/tool-catalog).
const SCOPE_LABELS = {
    'campaigns:read': 'Campaigns — read',
    'campaigns:write': 'Campaigns — write (proposal unless autonomous)',
    'ads:read': 'Ads & ad groups — read',
    'ads:write': 'Ads & ad groups — write (proposal unless autonomous)',
    'keywords:read': 'Keywords — read',
    'keywords:write': 'Keywords — write (proposal unless autonomous)',
    'sites:read': 'Sites — read',
    'sites:write': 'Sites — write (proposal unless autonomous)',
    'reports:read': 'Reports & analytics — read',
    'assets:read': 'Assets — read',
    'assets:write': 'Assets — write (proposal unless autonomous)',
    'sync:read': 'Sync — read',
    'sync:write': 'Sync — write (proposal unless autonomous)',
};

// Turn a snake_case tool name into a readable label.
const toolLabel = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AVAILABLE_CHANNELS = [
    { id: 'in_app', label: 'In-App Notification' },
    { id: 'email', label: 'Email' },
];

const AVAILABLE_LANGUAGES = [
    { code: 'de', name: 'German (Deutsch)' },
    { code: 'es', name: 'Spanish (Espanol)' },
    { code: 'fr', name: 'French (Francais)' },
    { code: 'it', name: 'Italian (Italiano)' },
    { code: 'pt', name: 'Portuguese (Portugues)' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' },
];


const AnalysisTaskDetailView = () => {
    const { id, tab } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Template state (Issue #64) - passed from TemplateGallery or TaskDescribeGenerate
    const templateState = location.state;
    const isNewFromTemplate = !id && templateState?.templateConfig;
    const isFromTemplateRoute = location.pathname === '/analysis-tasks/new/from-template';

    const getTabIndex = (tabName) => {
        const index = TAB_NAMES.indexOf(tabName);
        return index >= 0 ? index : 0;
    };

    const [activeTab, setActiveTab] = useState(getTabIndex(tab));
    const [task, setTask] = useState(null);
    const [formData, setFormData] = useState(() => {
        if (isNewFromTemplate) {
            const tpl = templateState.templateConfig;
            const sc = tpl.scheduleConfig;
            return {
                ...DEFAULT_FORM_DATA,
                ...tpl,
                // Unpack scheduleConfig into flat fields for ScheduleBuilder
                triggerType: sc?.triggerType || '',
                interval: sc?.interval || '',
                eventTriggers: sc?.eventTriggers || [],
                conditionRules: sc?.conditionRules || [],
                delayAfterTrigger: sc?.delayAfterTrigger || '',
                quietHoursStart: sc?.quietHoursStart || '',
                quietHoursEnd: sc?.quietHoursEnd || '',
            };
        }
        return { ...DEFAULT_FORM_DATA };
    });
    const [loading, setLoading] = useState(!isNewFromTemplate);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Testing state (Tab 5)
    const [testUserId, setTestUserId] = useState('');
    const [testRunning, setTestRunning] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testError, setTestError] = useState(null);

    // Subscription plans for audience tab
    const [subscriptionOptions, setSubscriptionOptions] = useState([]);

    // EdenAI config for model dropdown (Issue #65)
    const [edenConfig, setEdenConfig] = useState(null);

    // Tool Core catalog (fetched from the backend — single source of truth for
    // the tool picker, replacing the old hardcoded TOOLS_BY_TIER).
    const [toolCatalog, setToolCatalog] = useState({ tools: [], byScope: {} });

    // AI Assist panel state (Issue #64)
    const [aiAssistOpen, setAiAssistOpen] = useState(false);

    // Email subject translation state (Issue #66)
    const [selectedSubjectLang, setSelectedSubjectLang] = useState('de');
    const [isTranslatingSubject, setIsTranslatingSubject] = useState(false);

    // Template banner info (Issue #64)
    const [templateOrigin] = useState(
        isNewFromTemplate ? { name: templateState.templateName, id: templateState.templateId } : null
    );

    // Load task data
    const fetchTask = useCallback(async () => {
        if (isNewFromTemplate) {
            // For new tasks from template, just load subscription options
            try {
                const subsData = await mainApi.listPlans().catch(() => []);
                setSubscriptionOptions(subsData.options || subsData || []);
                // Set a fake task object so the UI renders
                setTask((prev) => ({ ...prev, id: 'new', name: prev?.name || 'New Task', status: 'paused' }));
            } catch { /* ignore */ }
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [taskData, subsData] = await Promise.all([
                adminApi.getAnalysisTask(id),
                mainApi.listPlans().catch(() => []),
            ]);

            const data = taskData.task || taskData;
            setTask(data);
            setFormData({
                ...DEFAULT_FORM_DATA,
                ...data,
                enabledTools: data.enabledTools || [],
                audienceRules: data.audienceRules || [],
                subscriptionPlans: data.subscriptionPlans || ['all'],
                channels: data.channels || ['in_app'],
                emailSubjectTemplate: data.emailSubjectTemplate || {},
                cooldownAfterEngagement: data.cooldownAfterEngagement || '4h',
                maxConsecutiveIgnored: data.maxConsecutiveIgnored || 3,
                // Unpack scheduleConfig into flat fields for ScheduleBuilder (Issue #83: use 'interval' not 'scheduleInterval')
                triggerType: data.scheduleConfig?.triggerType || '',
                interval: data.scheduleConfig?.interval || '',
                eventTriggers: data.scheduleConfig?.eventTriggers || [],
                conditionRules: data.scheduleConfig?.conditionRules || [],
                delayAfterTrigger: data.scheduleConfig?.delayAfterTrigger || '',
                quietHoursStart: data.scheduleConfig?.quietHoursStart || '',
                quietHoursEnd: data.scheduleConfig?.quietHoursEnd || '',
            });
            setSubscriptionOptions(subsData.options || subsData || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
        } finally {
            setLoading(false);
        }
    }, [id, isNewFromTemplate]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    // Redirect to template gallery if navigated to /new/from-template without valid state
    useEffect(() => {
        if (isFromTemplateRoute && !isNewFromTemplate) {
            navigate('/analysis-tasks/new/templates', { replace: true });
        }
    }, [isFromTemplateRoute, isNewFromTemplate, navigate]);

    // Fetch EdenAI config for model dropdown (Issue #65)
    useEffect(() => {
        adminApi.getEdenAIConfig()
            .then(data => setEdenConfig(data))
            .catch(() => { /* use fallback constants via getAllModels */ });
    }, []);

    // Fetch the Tool Core catalog for the enabled-tools picker.
    useEffect(() => {
        adminApi.getToolCatalog()
            .then(data => setToolCatalog({ tools: data.tools || [], byScope: data.byScope || {} }))
            .catch(() => { /* picker shows empty state on failure */ });
    }, []);

    // Sync tab from URL
    useEffect(() => {
        setActiveTab(getTabIndex(tab));
    }, [tab]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        navigate(`/analysis-tasks/${id}/${TAB_NAMES[newValue]}`, { replace: true });
    };

    // Update a single field
    const setField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Build payload: pack flat ScheduleBuilder fields into nested scheduleConfig (Issue #83)
    const buildPayload = (data) => {
        const payload = { ...data };
        // Build scheduleConfig from flat ScheduleBuilder fields
        const hasTriggerConfig = payload.triggerType && payload.triggerType !== '';
        if (hasTriggerConfig) {
            payload.scheduleConfig = {
                triggerType: payload.triggerType,
                interval: payload.interval || undefined,
                eventTriggers: payload.eventTriggers || [],
                conditionRules: payload.conditionRules || [],
                delayAfterTrigger: payload.delayAfterTrigger || undefined,
                quietHoursStart: payload.quietHoursStart || null,
                quietHoursEnd: payload.quietHoursEnd || null,
            };
        }
        // Remove flat fields that aren't part of the task model
        delete payload.triggerType;
        delete payload.interval;
        delete payload.eventTriggers;
        delete payload.conditionRules;
        delete payload.delayAfterTrigger;
        delete payload.quietHoursStart;
        delete payload.quietHoursEnd;
        return payload;
    };

    // Handle save
    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = buildPayload(formData);
            if (isNewFromTemplate) {
                // Create new task from template config
                const result = await adminApi.createAnalysisTask(payload);
                setSnackbar({ open: true, message: 'Task created successfully', severity: 'success' });
                const newId = result.id || result.task?.id;
                if (newId) {
                    navigate(`/analysis-tasks/${newId}/ai-config`, { replace: true });
                }
            } else {
                await adminApi.updateAnalysisTask(id, payload);
                setSnackbar({ open: true, message: 'Task saved successfully', severity: 'success' });
                fetchTask();
            }
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to save: ${message}`, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Handle clone (Issue #64)
    const handleClone = async () => {
        if (!id || isNewFromTemplate) return;
        try {
            const cloned = await adminApi.cloneAnalysisTask(id);
            const clonedId = cloned.id || cloned.task?.id;
            setSnackbar({ open: true, message: 'Task cloned successfully', severity: 'success' });
            if (clonedId) {
                navigate(`/analysis-tasks/${clonedId}/ai-config`);
            }
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to clone: ${message}`, severity: 'error' });
        }
    };

    // Handle AI assist suggestion apply (Issue #64)
    const handleApplySuggestion = (suggestion) => {
        if (!suggestion) return;
        setFormData(prev => {
            const updated = { ...prev };
            // Merge suggestion fields into form data
            for (const [key, value] of Object.entries(suggestion)) {
                if (key in DEFAULT_FORM_DATA || key === 'systemPrompt' || key === 'enabledTools' || key === 'dataAccessTier' || key === 'audienceRules' || key === 'emailSubjectTemplate') {
                    updated[key] = value;
                }
            }
            return updated;
        });
        setSnackbar({ open: true, message: 'AI suggestion applied', severity: 'success' });
    };

    // Handle validation fix apply (Issue #64)
    const handleApplyFix = (validationItem) => {
        if (validationItem.rule === 'write_tools_agent_message') {
            setField('notificationType', 'AGENT_RECOMMENDATION');
        }
    };

    // Handle test execution
    const handleRunTest = async () => {
        if (!testUserId.trim()) {
            setTestError('Please enter a user ID to test against.');
            return;
        }
        setTestRunning(true);
        setTestResult(null);
        setTestError(null);
        try {
            const result = await adminApi.testAnalysisTask(id, testUserId.trim());
            setTestResult(result);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setTestError(message);
        } finally {
            setTestRunning(false);
        }
    };

    // Handle tool checkbox toggle
    const handleToolToggle = (toolId) => {
        setFormData(prev => {
            const current = prev.enabledTools || [];
            const updated = current.includes(toolId)
                ? current.filter(t => t !== toolId)
                : [...current, toolId];
            return { ...prev, enabledTools: updated };
        });
    };

    // Handle channel toggle
    const handleChannelToggle = (channelId) => {
        setFormData(prev => {
            const current = prev.channels || [];
            const updated = current.includes(channelId)
                ? current.filter(c => c !== channelId)
                : [...current, channelId];
            return { ...prev, channels: updated };
        });
    };

    // Auto-translate email subject (Issue #66)
    const handleAutoTranslateSubject = async () => {
        const enSubject = (formData.emailSubjectTemplate || {}).en;
        if (!enSubject) {
            setSnackbar({ open: true, message: 'Please enter an English subject first.', severity: 'warning' });
            return;
        }
        setIsTranslatingSubject(true);
        try {
            const targetLangName = AVAILABLE_LANGUAGES.find(l => l.code === selectedSubjectLang)?.name || selectedSubjectLang;
            const response = await adminApi.generateEdenAI({
                systemMessage: 'You are a professional translator. Translate the email subject line to the target language. Keep template variables like {{userName}}, {{campaignName}}, {{taskName}} exactly as-is. Return a JSON object with a single key "subject" containing the translated subject line.',
                prompt: `Target Language: ${targetLangName}\n\nOriginal Subject: ${enSubject}\n\nTranslate the above email subject to ${targetLangName}. Keep all {{variable}} placeholders unchanged. Return only the JSON object.`,
                jsonMode: true,
            });

            let result = response.result;
            if (typeof result === 'string') {
                try {
                    const cleaned = result.replace(/```json\n?|```/g, '').trim();
                    result = JSON.parse(cleaned);
                } catch (e) {
                    throw new Error('AI returned invalid JSON format');
                }
            }

            if (result?.subject) {
                setField('emailSubjectTemplate', {
                    ...formData.emailSubjectTemplate,
                    [selectedSubjectLang]: result.subject,
                });
                setSnackbar({ open: true, message: `Subject translated to ${targetLangName}!`, severity: 'success' });
            }
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Translation failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setIsTranslatingSubject(false);
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading && !task) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!task && error) {
        return <Alert severity="error">{error.message}</Alert>;
    }

    if (!task) {
        return <Alert severity="error">Task not found.</Alert>;
    }

    return (
        <Box data-testid="analysis-task-detail-view">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/analysis-tasks')}
                    sx={{ mr: 2, color: 'text.secondary' }}
                    data-testid="analysis-task-detail-btn-back"
                >
                    Back
                </Button>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {formData.name || task.name}
                        <Chip
                            label={task.status || 'disabled'}
                            color={task.status === 'active' ? 'success' : task.status === 'paused' ? 'warning' : 'default'}
                            size="small"
                        />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        ID: {task.id}
                    </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setAiAssistOpen(true)}
                    sx={{ mr: 1 }}
                    data-testid="analysis-task-detail-btn-ai-assist"
                >
                    AI Assist
                </Button>
                {!isNewFromTemplate && (
                    <Button
                        startIcon={<ContentCopyIcon />}
                        onClick={handleClone}
                        sx={{ mr: 1 }}
                        data-testid="analysis-task-detail-btn-clone"
                    >
                        Clone
                    </Button>
                )}
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchTask}
                    sx={{ mr: 1 }}
                    data-testid="analysis-task-detail-btn-refresh"
                >
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="analysis-task-detail-btn-save"
                >
                    {saving ? 'Saving...' : isNewFromTemplate ? 'Create' : 'Save'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

            {/* Template Banner (Issue #64) */}
            {templateOrigin && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="template-banner">
                    Based on template: <strong>{templateOrigin.name}</strong>
                    {isNewFromTemplate && ' - Review and save to create this task.'}
                </Alert>
            )}

            {/* Semantic Validation Panel (Issue #64) */}
            <ValidationPanel formData={formData} onApplyFix={handleApplyFix} />

            {/* Tabs */}
            <Paper sx={{ mb: 3 }} data-testid="analysis-task-detail-tabs">
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    {TAB_LABELS.map((tabItem, index) => (
                        <Tab
                            key={index}
                            icon={tabItem.icon}
                            iconPosition="start"
                            label={tabItem.label}
                            data-testid={`analysis-task-tab-${TAB_NAMES[index]}`}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* TAB 0: AI Config */}
            {activeTab === 0 && (
                <Paper sx={{ p: 3 }} data-testid="analysis-task-panel-ai-config">
                    <Typography variant="h6" gutterBottom>General</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Task Name"
                                value={formData.name}
                                onChange={(e) => setField('name', e.target.value)}
                                required
                                data-testid="analysis-task-input-name"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => setField('description', e.target.value)}
                                data-testid="analysis-task-input-description"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>AI Configuration</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="System Prompt"
                                value={formData.systemPrompt}
                                onChange={(e) => setField('systemPrompt', e.target.value)}
                                multiline
                                rows={6}
                                placeholder="You are an AI assistant that analyzes Google Ads campaign data..."
                                helperText="The system prompt that defines the AI's behavior and analysis approach."
                                data-testid="analysis-task-input-system-prompt"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Response Style"
                                value={formData.responseStyle}
                                onChange={(e) => setField('responseStyle', e.target.value)}
                                data-testid="analysis-task-select-response-style"
                            >
                                <MenuItem value="concise">Concise</MenuItem>
                                <MenuItem value="detailed">Detailed</MenuItem>
                                <MenuItem value="actionable">Actionable</MenuItem>
                                <MenuItem value="executive_summary">Executive Summary</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Model"
                                value={formData.aiModel || ''}
                                onChange={(e) => setField('aiModel', e.target.value)}
                                helperText="Select a specific AI model or use the system default."
                                data-testid="analysis-task-input-model"
                            >
                                <MenuItem value=""><em>Use Default</em></MenuItem>
                                {getAllModels(edenConfig).map((model) => (
                                    <MenuItem key={`${model.provider}/${model.value}`} value={`${model.provider}/${model.value}`}>
                                        {model.label} ({model.provider})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Max Tokens"
                                value={formData.maxTokens}
                                onChange={(e) => setField('maxTokens', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 1, max: 4000, step: 100 }}
                                data-testid="analysis-task-input-max-tokens"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box data-testid="analysis-task-temperature-control">
                                <Typography variant="body2" gutterBottom>
                                    Temperature: {formData.temperature}
                                </Typography>
                                <Slider
                                    value={formData.temperature}
                                    onChange={(e, val) => setField('temperature', val)}
                                    data-testid="analysis-task-slider-temperature"
                                    min={0}
                                    max={2}
                                    step={0.1}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 0.7, label: '0.7' },
                                        { value: 1, label: '1' },
                                        { value: 2, label: '2' },
                                    ]}
                                    valueLabelDisplay="auto"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Lower = more focused, Higher = more creative
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* TAB 1: Tools & Data Access */}
            {activeTab === 1 && (
                <Paper sx={{ p: 3 }} data-testid="analysis-task-panel-tools-data">
                    <Typography variant="h6" gutterBottom>Tools & Data Access</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControl component="fieldset" data-testid="analysis-task-data-access-tier">
                                <FormLabel component="legend">Data Access Tier</FormLabel>
                                <RadioGroup
                                    value={formData.dataAccessTier}
                                    onChange={(e) => setField('dataAccessTier', e.target.value)}
                                    row
                                >
                                    <FormControlLabel value="profile" control={<Radio />} label="Profile - Basic user info only" />
                                    <FormControlLabel value="subscription" control={<Radio />} label="Subscription - Profile + subscription data" />
                                    <FormControlLabel value="content" control={<Radio />} label="Content - Campaign and ad content" />
                                    <FormControlLabel value="performance" control={<Radio />} label="Performance - Metrics and analytics" />
                                    <FormControlLabel value="external" control={<Radio />} label="External - All data including external APIs" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                data-testid="analysis-task-autonomous-toggle"
                                control={
                                    <Switch
                                        checked={!!formData.autonomous}
                                        onChange={(e) => setField('autonomous', e.target.checked)}
                                    />
                                }
                                label="Autonomous mode — execute write tools live (default off: writes become approval proposals)"
                            />
                            <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl component="fieldset" data-testid="analysis-task-enabled-tools">
                                <FormLabel component="legend">Enabled Tools</FormLabel>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                    Tools the AI may call, from the shared tool catalog (grouped by permission scope). Write tools run as approval proposals unless the task is autonomous.
                                </Typography>
                                {Object.keys(toolCatalog.byScope).length === 0 && (
                                    <Alert severity="info" sx={{ mt: 1 }}>No tools available.</Alert>
                                )}
                                {Object.entries(toolCatalog.byScope).map(([scope, tools]) => (
                                    <Box key={scope} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                                            {SCOPE_LABELS[scope] || scope}
                                        </Typography>
                                        <FormGroup>
                                            <Grid container>
                                                {tools.map((tool) => (
                                                    <Grid item xs={12} sm={6} md={4} key={tool.name}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={(formData.enabledTools || []).includes(tool.name)}
                                                                    onChange={() => handleToolToggle(tool.name)}
                                                                />
                                                            }
                                                            label={toolLabel(tool.name)}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </FormGroup>
                                    </Box>
                                ))}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* TAB 2: Audience (Issue #67 - Visual Rule Builder) */}
            {activeTab === 2 && (
                <AudienceRuleBuilder
                    formData={formData}
                    setField={setField}
                    subscriptionOptions={subscriptionOptions}
                />
            )}

            {/* TAB 3: Communication */}
            {activeTab === 3 && (
                <Paper sx={{ p: 3 }} data-testid="analysis-task-panel-communication">
                    <Typography variant="h6" gutterBottom>Communication Settings</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControl component="fieldset" data-testid="analysis-task-notification-channels">
                                <FormLabel component="legend">Notification Channels</FormLabel>
                                <FormGroup row>
                                    {AVAILABLE_CHANNELS.map((channel) => (
                                        <FormControlLabel
                                            key={channel.id}
                                            control={
                                                <Checkbox
                                                    checked={(formData.channels || []).includes(channel.id)}
                                                    onChange={() => handleChannelToggle(channel.id)}
                                                />
                                            }
                                            label={channel.label}
                                        />
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Notification Type"
                                value={formData.notificationType}
                                onChange={(e) => setField('notificationType', e.target.value)}
                                data-testid="analysis-task-select-notification-type"
                            >
                                <MenuItem value="AGENT_MESSAGE">Agent Message</MenuItem>
                                <MenuItem value="AGENT_RECOMMENDATION">Agent Recommendation</MenuItem>
                            </TextField>
                        </Grid>

                        {(formData.channels || []).includes('email') && (
                            <Grid item xs={12} data-testid="analysis-task-email-subject-editor">
                                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                    Email Subject Templates
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Define subject lines per language. Variables: {'{{userName}}'}, {'{{campaignName}}'}, {'{{taskName}}'}
                                </Typography>

                                {/* English subject (always visible, required) */}
                                <TextField
                                    fullWidth
                                    label="English Subject (required)"
                                    value={(formData.emailSubjectTemplate || {}).en || ''}
                                    onChange={(e) => setField('emailSubjectTemplate', {
                                        ...formData.emailSubjectTemplate,
                                        en: e.target.value,
                                    })}
                                    placeholder="Your {{campaignName}} analysis is ready"
                                    sx={{ mb: 2 }}
                                    data-testid="analysis-task-input-email-subject-en"
                                />

                                {/* Language selector + auto-translate */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <TextField
                                        select
                                        label="Add Language"
                                        value={selectedSubjectLang}
                                        onChange={(e) => setSelectedSubjectLang(e.target.value)}
                                        sx={{ minWidth: 200 }}
                                        size="small"
                                        data-testid="analysis-task-select-subject-lang"
                                    >
                                        {AVAILABLE_LANGUAGES.map((lang) => (
                                            <MenuItem key={lang.code} value={lang.code}>
                                                {lang.name}
                                                {(formData.emailSubjectTemplate || {})[lang.code] ? ' ✓' : ''}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={isTranslatingSubject ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                        onClick={handleAutoTranslateSubject}
                                        disabled={isTranslatingSubject || !(formData.emailSubjectTemplate || {}).en}
                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                        data-testid="analysis-task-btn-translate-subject"
                                    >
                                        {isTranslatingSubject ? 'Translating...' : 'Auto-Translate'}
                                    </Button>
                                </Box>

                                {/* Selected language subject field */}
                                <TextField
                                    fullWidth
                                    label={`Subject (${AVAILABLE_LANGUAGES.find(l => l.code === selectedSubjectLang)?.name || selectedSubjectLang})`}
                                    value={(formData.emailSubjectTemplate || {})[selectedSubjectLang] || ''}
                                    onChange={(e) => setField('emailSubjectTemplate', {
                                        ...formData.emailSubjectTemplate,
                                        [selectedSubjectLang]: e.target.value,
                                    })}
                                    placeholder={`Translated subject for ${AVAILABLE_LANGUAGES.find(l => l.code === selectedSubjectLang)?.name || selectedSubjectLang}`}
                                    size="small"
                                    sx={{ mb: 2 }}
                                    data-testid="analysis-task-input-email-subject-lang"
                                />

                                {/* Language chips showing configured languages */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.entries(formData.emailSubjectTemplate || {}).filter(([, v]) => v).map(([code]) => (
                                        <Chip
                                            key={code}
                                            label={code === 'en' ? 'English' : (AVAILABLE_LANGUAGES.find(l => l.code === code)?.name || code)}
                                            color={code === 'en' ? 'primary' : 'default'}
                                            variant={code === selectedSubjectLang ? 'filled' : 'outlined'}
                                            size="small"
                                            onClick={() => code !== 'en' && setSelectedSubjectLang(code)}
                                            onDelete={code !== 'en' ? () => {
                                                const updated = { ...formData.emailSubjectTemplate };
                                                delete updated[code];
                                                setField('emailSubjectTemplate', updated);
                                            } : undefined}
                                            data-testid={`analysis-task-chip-subject-${code}`}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Divider textAlign="left" sx={{ my: 1 }}>Anti-Spam Settings</Divider>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Cooldown After Engagement"
                                value={formData.cooldownAfterEngagement}
                                onChange={(e) => setField('cooldownAfterEngagement', e.target.value)}
                                helperText="Minimum time between notifications for the same user after engagement."
                                data-testid="analysis-task-input-cooldown-hours"
                            >
                                <MenuItem value="1h">1 Hour</MenuItem>
                                <MenuItem value="2h">2 Hours</MenuItem>
                                <MenuItem value="4h">4 Hours</MenuItem>
                                <MenuItem value="8h">8 Hours</MenuItem>
                                <MenuItem value="12h">12 Hours</MenuItem>
                                <MenuItem value="24h">24 Hours</MenuItem>
                                <MenuItem value="48h">48 Hours</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Max Notifications Per Day"
                                value={formData.maxNotificationsPerDay}
                                onChange={(e) => setField('maxNotificationsPerDay', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 3 }}
                                helperText="Maximum notifications a user can receive per day from this task (max 3)."
                                data-testid="analysis-task-input-max-notifications"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* TAB 4: Testing */}
            {activeTab === 4 && (
                <Paper sx={{ p: 3 }} data-testid="analysis-task-panel-testing">
                    <Typography variant="h6" gutterBottom>Test Execution</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Run this analysis task against a specific user to preview results. This will not send any notifications.
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="User ID"
                                value={testUserId}
                                onChange={(e) => setTestUserId(e.target.value)}
                                placeholder="Enter a user UID to test against"
                                helperText="The user's Firebase UID or internal user ID."
                                data-testid="analysis-task-test-input-user-id"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                startIcon={testRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                onClick={handleRunTest}
                                disabled={testRunning || !testUserId.trim()}
                                sx={{ height: 56 }}
                                data-testid="analysis-task-test-btn-run"
                            >
                                {testRunning ? 'Running...' : 'Run Test'}
                            </Button>
                        </Grid>

                        {testError && (
                            <Grid item xs={12}>
                                <Alert severity="error">{testError}</Alert>
                            </Grid>
                        )}

                        {testResult && (
                            <Grid item xs={12}>
                                <Card variant="outlined" data-testid="analysis-task-test-results">
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Test Results
                                        </Typography>
                                        {testResult.success !== undefined && (
                                            <Chip
                                                label={testResult.success ? 'Success' : 'Failed'}
                                                color={testResult.success ? 'success' : 'error'}
                                                size="small"
                                                sx={{ mb: 2 }}
                                            />
                                        )}
                                        {testResult.executionTimeMs && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                Execution time: {testResult.executionTimeMs}ms
                                            </Typography>
                                        )}
                                        {testResult.tokensUsed && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                Tokens: {testResult.tokensUsed}
                                            </Typography>
                                        )}

                                        <Divider sx={{ my: 2 }} />

                                        {testResult.analysis && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom>Analysis Output</Typography>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        component="pre"
                                                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
                                                    >
                                                        {typeof testResult.analysis === 'string'
                                                            ? testResult.analysis
                                                            : JSON.stringify(testResult.analysis, null, 2)}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        )}

                                        {testResult.notification && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>Notification Preview</Typography>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        component="pre"
                                                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
                                                    >
                                                        {typeof testResult.notification === 'string'
                                                            ? testResult.notification
                                                            : JSON.stringify(testResult.notification, null, 2)}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        )}

                                        {/* Fallback: show raw result if no specific fields */}
                                        {!testResult.analysis && !testResult.notification && (
                                            <Paper
                                                variant="outlined"
                                                sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    component="pre"
                                                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
                                                >
                                                    {JSON.stringify(testResult, null, 2)}
                                                </Typography>
                                            </Paper>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            )}

            {/* TAB 5: Schedule */}
            {activeTab === 5 && id && !isNewFromTemplate && (
                <>
                    <ScheduleBuilder formData={formData} setField={setField} />
                    <Box sx={{ mt: 3 }}>
                        <TaskScheduleTab taskId={id} task={task} />
                    </Box>
                </>
            )}
            {activeTab === 5 && isNewFromTemplate && (
                <Paper sx={{ p: 3 }}>
                    <Alert severity="info">Save the task first to configure schedules.</Alert>
                </Paper>
            )}

            {/* AI Assist Panel (Issue #64) */}
            <AiAssistPanel
                open={aiAssistOpen}
                onClose={() => setAiAssistOpen(false)}
                currentConfig={formData}
                onApplySuggestion={handleApplySuggestion}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnalysisTaskDetailView;
